/** What the page looked like at one moment. A killed page cannot report, so it leaves a trail. */
export interface CrashSample {
  /** Seconds since the page loaded. */
  at: number;
  path: string;
  scene: string;
  fps: number;
  worstFrameMs: number;
  domNodes: number;
  images: number;
  /** Image files fetched since the page loaded, which the browser may still hold decoded. */
  imagesFetched: number;
  /** Decoded size of every image on the page, the usual cause of a phone killing a tab. */
  imageMb: number;
  canvasMb: number;
  /** Only some browsers report this. iPhones do not. */
  heapMb: number | null;
  visible: boolean;
}

export interface CrashSession {
  startedAt: string;
  build: string;
  device: string;
  screen: string;
  samples: CrashSample[];
  /** Errors and lost graphics, newest last. */
  events: string[];
  ended: boolean;
}

type LogStorage = Pick<Storage, "getItem" | "setItem" | "removeItem">;

const SESSION_KEY = "yarn-crash-session";
const REPORTS_KEY = "yarn-crash-reports";
const KEPT_SAMPLES = 60;
const KEPT_EVENTS = 20;
const KEPT_REPORTS = 5;

function read<T>(storage: LogStorage, key: string): T | null {
  try {
    return JSON.parse(storage.getItem(key) ?? "null") as T | null;
  } catch {
    return null;
  }
}

function write(storage: LogStorage, key: string, value: unknown): void {
  try {
    storage.setItem(key, JSON.stringify(value));
  } catch {
    // Private windows and full storage must never break the app.
  }
}

/** A page closed on purpose says so. One that was on screen and never said so was killed. */
function diedOnScreen(session: CrashSession | null): session is CrashSession {
  return session !== null && !session.ended && session.samples.at(-1)?.visible !== false;
}

export function crashReports(storage: LogStorage): CrashSession[] {
  return read<CrashSession[]>(storage, REPORTS_KEY) ?? [];
}

export function clearCrashReports(storage: LogStorage): void {
  storage.removeItem(REPORTS_KEY);
}

export interface CrashLog {
  sample: (sample: CrashSample) => void;
  event: (text: string) => void;
  end: () => void;
  resume: () => void;
}

/** Files the last session if it was killed, then starts recording this one. */
export function startCrashLog(
  storage: LogStorage,
  info: Pick<CrashSession, "startedAt" | "build" | "device" | "screen">,
): CrashLog {
  const previous = read<CrashSession>(storage, SESSION_KEY);
  if (diedOnScreen(previous)) {
    write(storage, REPORTS_KEY, [previous, ...crashReports(storage)].slice(0, KEPT_REPORTS));
  }
  const session: CrashSession = { ...info, samples: [], events: [], ended: false };
  const save = (): void => write(storage, SESSION_KEY, session);
  save();
  return {
    sample: (sample) => {
      session.samples = [...session.samples, sample].slice(-KEPT_SAMPLES);
      save();
    },
    event: (text) => {
      session.events = [...session.events, text].slice(-KEPT_EVENTS);
      save();
    },
    end: () => {
      session.ended = true;
      save();
    },
    resume: () => {
      session.ended = false;
      save();
    },
  };
}
